import React, { useState } from "react";
import {
  Flex,
  Box,
  TableContainer,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  Badge,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Text,
  Icon,
  HStack,
} from "@chakra-ui/react";
import { Calendar, ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { useAppContext } from "../../../contexts/AppContext";
import TableFilters from "../../general/TableFilters";

const getQuarter = (date) => {
  const month = new Date(date).getMonth(); // 0-indexed
  return `Q${Math.floor(month / 3) + 1}`;
};

const getYear = (date) => new Date(date).getFullYear();

const ReportsTable = ({ fund, reportManager }) => {
  const { formatDate, tableSize, getFileIcon } = useAppContext();
  const [searchTerm, setSearchTerm] = useState("");
  const [quarterOffset, setQuarterOffset] = useState(0);

  const now = new Date();
  const currentQuarter = Math.floor(now.getMonth() / 3) + 1;
  const baseYear = now.getFullYear();
  const quarterDate = new Date(baseYear, currentQuarter * 3 - 3); // start of quarter
  quarterDate.setMonth(quarterDate.getMonth() + quarterOffset * 3);

  const selectedQuarter = `Q${Math.floor(quarterDate.getMonth() / 3) + 1}`;
  const selectedYear = quarterDate.getFullYear();

  const [filters, setFilters] = useState({});
  const labelMap = {
    category: "Category",
  };
  const filterOptions = {
    category: [
      "Annex IV",
      "Risk Management",
      "ESG",
      "AIFM",
      "PM",
      "Valuation",
      "Custom",
    ],
  };

  const filteredReports = (fund?.factSheet?.reports || [])
    .map((report) => ({
      ...report,
      fundName: fund.fundName,
      date: report.date || new Date().toISOString(),
    }))
    .filter((report) => {
      const matchesName = report.name.toLowerCase().includes(searchTerm);
      const matchesCategory =
        !filters.category ||
        filters.category.length === 0 ||
        filters.category.includes(report.category);

      // Si une recherche est active => ignorer le filtre de trimestre
      if (searchTerm.length > 0) {
        return matchesName && matchesCategory;
      }

      // Sinon : filtrer aussi par trimestre sélectionné
      const isInQuarter =
        getQuarter(report.date) === selectedQuarter &&
        getYear(report.date) === selectedYear;

      return matchesName && matchesCategory && isInQuarter;
    });

  return (
    <Flex direction="column" gap={8}>
      {/* Search + Add */}
      <Flex gap={2} flex={1} justifyContent={"space-between"}>
        
        <HStack>
          <Input
            placeholder="Search reports by name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value.toLowerCase())}
            flex={1}
            w={"260px"}
          />
          <TableFilters
            filters={filters}
            setFilters={setFilters}
            filterOptions={filterOptions}
            labelMap={labelMap}
          />
        </HStack>

        <HStack>
        <Flex
          align="center"
          justify="center"
          gap={4}
          border="1px solid"
          borderColor="grey.200"
          borderRadius="md"
          pl={4}
          pr={1}
          h="37.5px"
          opacity={searchTerm ? 0.5 : 1}
          pointerEvents={searchTerm ? "none" : "auto"}>
          <HStack spacing={2}>
            <Icon as={Calendar} size={16} color="grey.600" />
            <Text fontWeight="600" fontSize="md" color="grey.800">
              {selectedQuarter} {selectedYear}
            </Text>
          </HStack>
          <Flex>
            <IconButton
              icon={<ChevronLeft />}
              variant="ghost"
              size="sm"
              onClick={() => setQuarterOffset((prev) => prev - 1)}
              aria-label="Previous quarter"
            />
            <IconButton
              icon={<ChevronRight />}
              variant="ghost"
              size="sm"
              onClick={() => setQuarterOffset((prev) => prev + 1)}
              aria-label="Next quarter"
            />
          </Flex>
        </Flex>

        <Menu>
          <MenuButton as={Button} leftIcon={<Plus size={16} />}>
            New report
          </MenuButton>
          <MenuList>
            <MenuItem
              onClick={() =>
                reportManager.openFullScreenReportModal(
                  fund.fundName,
                  "Annex IV"
                )
              }>
              Annex IV
            </MenuItem>
            <MenuItem>Risk</MenuItem>
            <MenuItem>ESG</MenuItem>
            <MenuItem>Valuation</MenuItem>
            <MenuItem>AIFM</MenuItem>
            <MenuItem>PM</MenuItem>
            <MenuItem>Custom</MenuItem>
          </MenuList>
        </Menu>
        </HStack>
      </Flex>

      {/* Table */}

        <TableContainer>
          <Table variant="simple" size={tableSize}>
            <Thead>
              <Tr>
                <Th>Report Name</Th>
                <Th>Category</Th>
                <Th>Date</Th>
              </Tr>
            </Thead>
            <Tbody>
            {filteredReports.length > 0 ? (
              filteredReports.map((report, index) => (
                <Tr key={index}>
                  <Td fontWeight="500">
                    <Flex align="center" gap={4}>
                      <Box w="20px" h="20px">
                        {getFileIcon(report.name)}
                      </Box>
                      {report.name}
                    </Flex>
                  </Td>
                  <Td>
                    <Badge colorScheme="blue">{report.category}</Badge>
                  </Td>
                  <Td><Badge colorScheme="transparent">{formatDate(report.date)}</Badge></Td>
                </Tr>
              ))
            ) : (

              <Tr>
                              <Td colSpan="3">
                                <Text textAlign="center" py={10} color="gray.500">
                                No reports found for {selectedQuarter} {selectedYear} with these filters.
                                </Text>
                              </Td>
                            </Tr>
              
            )}
            </Tbody>
          </Table>
        </TableContainer>
      
    </Flex>
  );
};

export default ReportsTable;
